import * as React from 'react'
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import IconBack from '../assets/images/icons/icon-player-back.svg'
import IconForward from '../assets/images/icons/icon-player-forward.svg'
import IconNext from '../assets/images/icons/icon-player-next.svg'
import IconPrevious from '../assets/images/icons/icon-player-previous.svg'
import IconPause from '../assets/images/icons/icon-player-pause.svg'
import IconPlay from '../assets/images/icons/icon-player-play.svg'
import SliderUnstyled, { sliderUnstyledClasses } from '@mui/base/SliderUnstyled'
import { useCookieConsent } from '../utils/customHooks'
import { trackAudio } from '../utils/tracking'


const PlayerControlsButtonRowStyled = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PrevNextButtonStyled = styled.button`
  position: relative;
  margin: 0 17px 0 17px;

  ${props => props.disabled === true && css`
    pointer-events: none;
    opacity: 0.15;
  `}
`

const PlayHeadButtonStyled = styled.button`
  ${props => props.theme.colorBackgroundDarkblue && css`
  `}
  position: relative;
  margin: 0 17px 0 17px;
`

const PlayButtonStyled = styled.button`
  position: relative;

  img {
    transition: opacity ease-out 0.3s;
  }
`

const PlayerControlsStyled = styled.div<{ isSticky: boolean, isActive: boolean }>`
  width: 100%;
  height: ${props => props.theme.playerControlsHeight}px;
  min-height: ${props => props.theme.playerControlsHeight}px;
  background-color: ${props => props.theme.colorBackgroundDarkblue};

  button {
    color: #ffffff;
  }

  ${props => props.isSticky && css`
    position: sticky;
    bottom: 0;
  `}

  ${props => props.isActive === false && css`
    * {
      pointer-events: none;
      opacity: 0.5;
    }
  `}
`

/**
 *
 * During the loading process of an audio/video, the following events occur, in this order:
 *
 * 1.loadstart
 * 2.durationchange
 * 3.loadedmetadata
 * 4.loadeddata
 * 5.progress
 * 6.canplay
 * 7.canplaythrough
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
 *
 * @param src
 * @param audio
 * @param isSticky
 * @constructor
 */


const ScrubBarTimeStyled = styled.span`
  pointer-events: none;
  position: absolute;
  top: 38px;
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-weight: 600; // semi bold
  font-size: 11px;
  pointer-events: none;

  &:nth-child(2) {
    left: 30px;
  }

  &:nth-child(3) {
    right: 30px;
  }
`

const SliderStyled = styled(SliderUnstyled)`
  position: relative;
  color: #ffffff;
  height: 6px;
  width: 100%;
  padding: 11px 0 0 0;
  display: block;

  cursor: pointer;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    opacity: 1;
  }

  &.${sliderUnstyledClasses.disabled} {
    pointer-events: none;
    cursor: default;
    color: grey;
    opacity: 0.5;
  }

  & .${sliderUnstyledClasses.rail} {
    display: block;
    position: absolute;
    width: 100%;
    height: 4px;
    border-radius: 0;
    background-color: currentColor;
    opacity: 0.2;
  }

  & .${sliderUnstyledClasses.track} {
    display: block;
    position: absolute;
    height: 4px;
    border-radius: 0;
    opacity: 1;
    transition: opacity 0.3s ease-out;
    background-color: currentColor;
  }

  & .${sliderUnstyledClasses.thumb} {
    position: absolute;
    width: 10px; //16px
    height: 10px;
    margin-left: -3px; //-6px
    margin-top: -3px;
    box-sizing: border-box;
    border-radius: 50%;
    outline: 0;
    border: 3px solid currentColor;
    background-color: #fff;
  }
`

const ScrubBarContainerStyled = styled.div<{ isLoading: boolean }>`
  position: relative;
  //height: 55px;

  padding: 16px 30px 25px 30px; { /* needing padding, cause margin doesn't work with fixed element on overview page */

  > ${SliderStyled} .${sliderUnstyledClasses.track} {
    ${props => props.isLoading === true && css`
      opacity: 0.6;
    `}
  }
}
`

// vom thumb rauskopiert
//   :hover,
//   &.${sliderUnstyledClasses.focusVisible} {
//     box-shadow: 0 0 0 0.25rem (
//   theme.palette.mode === 'light' ? blue[400] : blue[300],
//   0.15
// )};
//   }
//
//   &.${sliderUnstyledClasses.active} {
//     box-shadow: 0 0 0 0.25rem (
//   theme.palette.mode === 'light' ? blue[200] : blue[300],
//   0.3
// )};
//   }


export const AudioPlayer: FunctionComponent<{
  src: string, audio: HTMLAudioElement,
  shouldPlayOnStart: boolean;
  isSticky: boolean,
  onPreviousClick(): void, onNextClick(): void, onCurrentTimeChange?(value: number): void,
  onEnded?(isPlaying: boolean): void, initialCurrentTime?: number,
  btnNextDisabled: boolean, btnPreviousDisabled: boolean,
}> = ({
        src, audio, isSticky, onPreviousClick, onNextClick, onCurrentTimeChange,
        onEnded, shouldPlayOnStart, initialCurrentTime = 0, btnNextDisabled = false, btnPreviousDisabled = false
      }) => {
  // states
  const [duration, _setDuration] = useState(0)
  const [isFirstCanPlay, setIsFirstCanPlay] = useState(true)
  const [isPlaying, _setIsPlaying] = useState(false)
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false)
  const [currentTime, _setCurrentTime] = useState(0)
  const [canAudioPlay, setCanAudioPlay] = useState(false) // after each seek, audio may be preloaded a litte bit before it can be played
  const { isUserExperienceCookiesAllowed } = useCookieConsent()
  const [sliderValue, setSliderValue] = useState(0)
  const [isIsSliderDragged, setIsIsSliderDragged] = useState(false)

  const [text, updateText] = useState('')
  const isFirstCanPlayRef = useRef<boolean>(true)
  const isPlayingRef = useRef<boolean>(false)
  const sliderValueRef = useRef<number>(0)

  useEffect(() => {
    isFirstCanPlayRef.current = isFirstCanPlay // Write it to the ref
    isPlayingRef.current = isPlaying
    sliderValueRef.current = sliderValue
  })

  // set volume to what?
  const setCurrentTime = (seconds) => {
    _setCurrentTime(seconds)
    if (onCurrentTimeChange)
      onCurrentTimeChange(seconds)
  }

  const playAudioPromise = (): void => {
    const promise = audio.play()
    promise.then(_ => {
      // show play button
      // setAudioPlaying(true)
    }).catch(error => {
      console.log('info: playAudioPromise: ', error)
      if (error.code === 0) { // failed because the user didn't interact with the document first.
        // setAudioPlaying(false)
        setIsPlaying(false)
      } else { // e.g. The play() request was interrupted by a new load request.
        // console.log('error', error.code, error.message)
      }
      // show pause button
    })
  }

  const setDuration = (seconds) => {
    _setDuration(seconds)
  }

  const setIsPlaying = (value) => {
    _setIsPlaying(value)
    if (value) {
      playAudioPromise()
    } else
      audio.pause()
  }


  const togglePlayPause = (): void => {
    if (audio.currentTime === audio.duration) { // if ended, we want to start on same audio again
      audio.currentTime = 0
    }
    setIsPlaying(!isPlaying)
    // onIsPlayingChange(!initialIsPlaying)
    trackAudio(isUserExperienceCookiesAllowed, !isPlaying ? 'playClick' : 'pauseClick', src, false)
  }

  const rewindPlayHead = () => {
    audio.currentTime = Math.max(audio.currentTime - 15, 0)
    trackAudio(isUserExperienceCookiesAllowed, 'rewind15secClick', src, false)
  }

  const forwardPlayHead = () => {
    const newCurrentTime = Math.min(audio.currentTime + 15, audio.duration)
    if (newCurrentTime === audio.duration) {
      setIsPlaying(false)
    }
    audio.currentTime = newCurrentTime
    trackAudio(isUserExperienceCookiesAllowed, 'forward15secClick', src, false)
  }

  // We use useCallback here because it's an event handler that must be removed on unmount
  // See: What is useCallback in React and when to use it?
  // https://stackoverflow.com/questions/71265042/what-is-usecallback-in-react-and-when-to-use-it
  //
  const onAudioEnded = useCallback((e) => {
    setIsPlaying(false) // this is needed for the last track, but won't harm for the other tracks as well
    onEnded && onEnded(isPlayingRef.current)
  }, [isPlayingRef, onEnded])

  const onAudioTimeUpdate = useCallback((e) => { // duration may be NaN
    setCurrentTime(audio.currentTime)
    // console.log('current time update: ', audio.currentTime)
  }, [])

  const onAudioProgress = (e) => {
    // console.log('on audio progress')
  }

  const onAudioPlay = (e) => {
    _setIsPlaying(true)
  }

  const onAudioPause = (e) => {
    _setIsPlaying(false)
  }

  const onAudioLoadedData = useCallback(() => { // first we get timeUpdate and then durationUpdate
    // console.log('onAudioLoadedData state: ', audio.readyState, ', src: ', audio.src, ', initialCurrentTime: ', initialCurrentTime)
    if (audio.src.indexOf(src) === -1) return // bugfix: we sadly get event of old audio element, this maybe is not our bug
    if (audio.readyState >= 1) { // 1 == HAVE_METADATA
      setIsMetadataLoaded(true) // now we can seek
      // cause if ios we need to set currentTime here and before setting play! Otherwise we could not set currentTime
      // console.log('on audio loaded data ready >=1, duration: ', audio.duration, ', currrentTime: ', audio.currentTime, ', src: ', audio.src, ', src: ', src)
      if (sliderValueRef.current) { // we prefer what user has currently set
        audio.currentTime = sliderValueToCurrentTime(sliderValueRef.current)
      } else if (initialCurrentTime) {
        audio.currentTime = initialCurrentTime
        // if (initialCurrentTime + 15 >= audio.duration) { // needed if we go to previous track while audio isPlaying (thus going to previous track would become impossible, cause onEnded() we jump to next rack
        //   onIsPlayingChange(false)
        // }
      }
    }
    // if (audio.readyState >= 2) { // now audio can play
    // }
  }, [src, initialCurrentTime, sliderValueRef])

  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return ''
    const min = Math.floor(seconds / 60)
    const sec = Math.floor(seconds % 60)
    return `${min}:${sec < 10 ? '0' + sec : sec}`
  }

  const sliderValueToCurrentTime = (value) => {
    return audio.duration / 100 * value
  }

  const currentTimeToSliderValue = () => {
    return (audio.currentTime >= 0 && audio.duration >= 0) ? audio.currentTime / audio.duration * 100 : 0
  }

  const onAudioDurationChange = useCallback((e) => {
    // console.log('on audio duration change: ', audio.duration)
    const seconds = Math.floor(audio.duration)
    setDuration(seconds)
  }, [])

  const onAudioCanPlay = useCallback((e) => {
    // console.log('onAudioCanPlay') //on iphone this events is called only first time, not after seeking! on other devices is also called after each seek

    if (isFirstCanPlayRef.current) {
      setIsFirstCanPlay(false)
      setIsPlaying(shouldPlayOnStart)
      setCanAudioPlay(true)
    }
  }, [isFirstCanPlayRef, shouldPlayOnStart])

  const onSliderChangeCommitted = (e, value) => {
    trackAudio(isUserExperienceCookiesAllowed, 'seekClick', src, false, (value as number))
    setIsIsSliderDragged(false)
    audio.currentTime = sliderValueToCurrentTime(value as number)
  }
  const onSliderChange = (e, value) => {
    // console.log('on slider change')
    // audio.currentTime = audio.duration / 100 * (value as number)
    setIsIsSliderDragged(true)
    setSliderValue(value)
  }

  // how to access latest state: https://github.com/facebook/react/issues/14699

  useEffect(() => {
    // reset states
    setIsFirstCanPlay(true)
    setIsIsSliderDragged(false)
    setSliderValue(0)
    setCanAudioPlay(false)
    setCurrentTime(0)
    _setIsPlaying(false)
    setIsMetadataLoaded(false) // new
    _setDuration(0)
    audio = audio ? audio : new HTMLAudioElement()
    audio.preload = 'auto'

    onAudioLoadedData()
    if (src) {
      audio.src = src
    }
    // if (initialCurrentTime) { // this does not work on iphone, we wait for isMetadataLoaded
    //   audio.currentTime = initialCurrentTime
    // }
    // canplay: enough of the file has downloaded to start playing
    // onCanPlayThrough: entire file has downloaded
    // abort: unloading the player (switching to another src)
    // seeked: user seeks
    // volumeChange: volume changed
    // error: error occurred
    audio.addEventListener('ended', onAudioEnded)
    // audio.addEventListener('ended', onAudioEnded)
    audio.addEventListener('timeupdate', onAudioTimeUpdate)
    audio.addEventListener('progress', onAudioProgress)
    audio.addEventListener('loadeddata', onAudioLoadedData)
    audio.addEventListener('durationchange', onAudioDurationChange)
    audio.addEventListener('canplay', onAudioCanPlay)
    audio.addEventListener('play', onAudioPlay)
    audio.addEventListener('pause', onAudioPause)
    audio.load()

    return () => {
      audio.pause()
      audio.removeEventListener('ended', onAudioEnded)
      audio.removeEventListener('timeupdate', onAudioTimeUpdate)
      audio.removeEventListener('progress', onAudioProgress)
      audio.removeEventListener('loadeddata', onAudioLoadedData)
      audio.removeEventListener('durationchange', onAudioDurationChange)
      audio.removeEventListener('canplay', onAudioCanPlay)
      audio.removeEventListener('play', onAudioPlay)
      audio.removeEventListener('pause', onAudioPause)
    }
  }, [src])

  return (
    <>
      <PlayerControlsStyled isSticky={isSticky} isActive={isMetadataLoaded}>
        <ScrubBarContainerStyled isLoading={false}>
          <SliderStyled
            value={isIsSliderDragged ? sliderValue : currentTimeToSliderValue()}
            onChange={onSliderChange} onChangeCommitted={onSliderChangeCommitted} />
          <ScrubBarTimeStyled>{isIsSliderDragged ? formatTime(sliderValueToCurrentTime(sliderValue)) : formatTime(audio.currentTime)}</ScrubBarTimeStyled>
          <ScrubBarTimeStyled>{formatTime(audio.duration)}</ScrubBarTimeStyled>
        </ScrubBarContainerStyled>

        <PlayerControlsButtonRowStyled>
          {isPlaying && (
            <PlayHeadButtonStyled type="button"
                                  aria-label="rewind play head"
                                  onClick={rewindPlayHead}
            >
              <img alt="back" src={IconBack} width="45px" height="45px" />
            </PlayHeadButtonStyled>
          )}
          {!isPlaying && (
            <PrevNextButtonStyled
              type="button"
              aria-label="previous station"
              onClick={onPreviousClick}
              disabled={btnPreviousDisabled && audio.currentTime === 0}>
              <img alt="previous" src={IconPrevious} width="45px" height="45px" />
            </PrevNextButtonStyled>
          )}
          <PlayButtonStyled
            aria-label={isPlaying ? 'pause' : 'play'}
            type="button"
            onClick={togglePlayPause}
          >
            {isPlaying ? <img alt="pause" src={IconPause} width="62px" height="62px" /> : <img alt="play" src={IconPlay} width="62px" height="62px" />}

          </PlayButtonStyled>
          {!isPlaying && (
            <PrevNextButtonStyled
              type="button"
              aria-label="next station"
              onClick={onNextClick}
              disabled={btnNextDisabled}
            >
              <img alt="next" src={IconNext} width="45px" height="45px" />
            </PrevNextButtonStyled>
          )}
          {isPlaying && (
            <PlayHeadButtonStyled
              type="button"
              aria-label="forward play head"
              onClick={forwardPlayHead}
            >
              <img alt="forward" src={IconForward} width="45px" height="45px" />
            </PlayHeadButtonStyled>
          )}
        </PlayerControlsButtonRowStyled>
      </PlayerControlsStyled>
    </>

    /**
     * <InputRange
     *         disabled={this.props.disabled}
     *         draggableTrack={this.props.draggableTrack}
     *         maxValue={this.timeToMinute(this.props.maxValue)}
     *         minValue={this.timeToMinute(this.props.minValue)}
     *         onChangeStart={this.onChangeStart.bind(this)}
     *         onChange={this.onChange.bind(this)}
     *         onChangeComplete={this.onChangeComplete.bind(this)}
     *         step={this.props.step}
     *         value={{min: min, max: max}}/>);
     *   }
     */
  )
}

